import { useMemo } from 'react'
import { Edge, Node } from 'reactflow'
import { RoadNodeWithActions, RoadSegmentWithActions } from '../models/yard-view.model'
import { RoadNetworkViewStore } from '../stores/nodes/road-network.view-store'
import { YardViewStore } from '../stores/yard.view-store'

interface RoadNetworkNodes {
  roadNodeNodes: Node<RoadNodeWithActions>[]
  roadSegmentEdges: Edge<RoadSegmentWithActions>[]
}

export const useRoadNetworkNodes = (
  viewStore: YardViewStore,
  roadNetworkViewStore: RoadNetworkViewStore,
): RoadNetworkNodes => {
  const roadNodeNodes = useMemo<Node<RoadNodeWithActions>[]>(
    () =>
      roadNetworkViewStore.nodes.map(node => ({
        id: `road-node-${node.id}`,
        type: 'roadNode',
        zIndex: 0,
        position: node.layoutPosition,
        data: {
          ...node,
          isPending: node.id === roadNetworkViewStore.pendingNodeId,
          onDelete: () => {
            // TODO Implement once API is ready
            return
          },
          onContextMenu: (id: string, position: { x: number; y: number }) => {
            if (!roadNetworkViewStore.isDrawingMode) return
            if (roadNetworkViewStore.pendingNodeId) {
              roadNetworkViewStore.clearPendingNode()
            } else {
              roadNetworkViewStore.openContextMenu(id, 'node', position)
            }
          },
        },
        draggable: !viewStore.isViewMode && roadNetworkViewStore.isDrawingMode,
        selectable: !viewStore.isViewMode && roadNetworkViewStore.isDrawingMode,
      })),
    [
      roadNetworkViewStore.nodes,
      roadNetworkViewStore,
      viewStore.isViewMode,
      roadNetworkViewStore.isDrawingMode,
      roadNetworkViewStore.pendingNodeId,
    ],
  )

  const roadSegmentEdges = useMemo<Edge<RoadSegmentWithActions>[]>(
    () =>
      roadNetworkViewStore.segments.map(segment => ({
        id: `road-segment-${segment.id}`,
        source: `road-node-${segment.startNodeId}`,
        target: `road-node-${segment.endNodeId}`,
        type: 'roadSegment',
        zIndex: 0,
        data: {
          ...segment,
          onDelete: () => {
            // TODO Implement once API is ready
            return
          },
          onUpdateDirection: () => {
            // TODO Implement once API is ready
            return
          },
          onUpdateBlocked: () => {
            // TODO Implement once API is ready
            return
          },
          onUpdateWeightLimit: () => {
            // TODO Implement once API is ready
            return
          },
          onContextMenu: (id: string, position: { x: number; y: number }) => {
            if (!roadNetworkViewStore.isDrawingMode) return
            if (roadNetworkViewStore.pendingNodeId) {
              roadNetworkViewStore.clearPendingNode()
            } else {
              roadNetworkViewStore.openContextMenu(id, 'segment', position)
            }
          },
          onSplitSegment: (id: string, position: { x: number; y: number }) => {
            if (!roadNetworkViewStore.isDrawingMode) return
            roadNetworkViewStore.splitSegment(id, position)
          },
          isDrawingWithPending:
            roadNetworkViewStore.isDrawingMode && !!roadNetworkViewStore.pendingNodeId,
        },
      })),
    [
      roadNetworkViewStore.segments,
      roadNetworkViewStore,
      roadNetworkViewStore.isDrawingMode,
      roadNetworkViewStore.pendingNodeId,
      viewStore.isViewMode,
    ],
  )

  return { roadNodeNodes, roadSegmentEdges }
}
